// Constants and Configs matching Indian Grid Standards (2026 update)
export const SOLAR_CONFIG = {
  rates: {
    residential: 7.5,
    commercial: 8.5,
    industrial: 9.0,
  },
  costsPerKw: {
    residential: 62000,
    commercial: 58000,
    industrial: 52000,
  },
  // PM Surya Ghar: Muft Bijli Yojana subsidy slab mapping
  subsidy: {
    applicableType: 'residential',
    slabs: [
      { upToKw: 2, subsidyPerKw: 30000 },
      { upToKw: 3, subsidyPerKw: 18000 }, // Over 2kW and up to 3kW is ₹18,000/kW
    ],
    maxTotalSubsidy: 78000, // Maximum cap in India
  },
  generationFactorPerKwMonthly: 120, // 1kW generates approx 120 units/month in India
  lifetimeYears: 25,
};

/**
 * Indian Currency Formatter (en-IN)
 */
export const formatIndianCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Calculates dynamic government subsidy structure
 */
export const calculateSubsidy = (capacity, propertyType) => {
  if (propertyType !== SOLAR_CONFIG.subsidy.applicableType) return 0;

  let totalSubsidy = 0;
  const cap = Math.min(capacity, 3); // Subsidy capped up to 3kW mostly

  if (cap <= 2) {
    totalSubsidy = cap * SOLAR_CONFIG.subsidy.slabs[0].subsidyPerKw;
  } else {
    totalSubsidy = (2 * SOLAR_CONFIG.subsidy.slabs[0].subsidyPerKw) + 
                   ((cap - 2) * SOLAR_CONFIG.subsidy.slabs[1].subsidyPerKw);
  }

  return Math.min(totalSubsidy, SOLAR_CONFIG.subsidy.maxTotalSubsidy);
};

/**
 * Master Core Solar Sizing Engine
 */
export const runSolarAssessment = (inputMode, inputValue, propertyType) => {
  const perUnitRate = SOLAR_CONFIG.rates[propertyType] || 7.5;
  
  // Calculate or assign Monthly Units
  let monthlyUnits = 0;
  if (inputMode === 'bill') {
    monthlyUnits = inputValue / perUnitRate;
  } else {
    monthlyUnits = inputValue;
  }

  // Sizing matrix logic
  const requiredCapacity = monthlyUnits / SOLAR_CONFIG.generationFactorPerKwMonthly;
  const roundedCapacity = Math.round(requiredCapacity * 10) / 10; // Round to 1 decimal place

  // Generation metrics
  const monthlyGeneration = roundedCapacity * SOLAR_CONFIG.generationFactorPerKwMonthly;
  const annualGeneration = monthlyGeneration * 12;

  // Financial Costings
  const costPerKw = SOLAR_CONFIG.costsPerKw[propertyType];
  const grossCost = roundedCapacity * costPerKw;
  const subsidyAmount = calculateSubsidy(roundedCapacity, propertyType);
  const netInvestment = Math.max(0, grossCost - subsidyAmount);

  // Offset & Savings parameters
  const actualUnitsOffset = Math.min(monthlyUnits, monthlyGeneration);
  const monthlySavings = actualUnitsOffset * perUnitRate;
  const annualSavings = monthlySavings * 12;

  // Payback Amortization & Lifetime Metrics
  const paybackPeriod = annualSavings > 0 ? (netInvestment / annualSavings) : 0;
  const lifetimeSavings = (annualSavings * SOLAR_CONFIG.lifetimeYears) - netInvestment;

  return {
    monthlyConsumption: Math.round(monthlyUnits),
    recommendedCapacity: roundedCapacity,
    monthlyGeneration: Math.round(monthlyGeneration),
    annualGeneration: Math.round(annualGeneration),
    grossCost: Math.round(grossCost),
    subsidyAmount: Math.round(subsidyAmount),
    netInvestment: Math.round(netInvestment),
    monthlySavings: Math.round(monthlySavings),
    annualSavings: Math.round(annualSavings),
    paybackPeriod: requiredCapacity > 0 ? paybackPeriod.toFixed(1) : '0.0',
    lifetimeSavings: Math.round(lifetimeSavings),
  };
};